import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UserSettings } from '../../lib/api/settings/user-settings-api-call';

export enum DashboardViews {
  home,
  products,
  orders,
  statistics,
  settings,
}

export interface UIState {
  dashboard: {
    currentView: DashboardViews;
  };
  theme: {
    type: string;
    dark: boolean;
  };
  isSidebarMinimized: boolean;
  isSidebarDrawerOpen: boolean;
  sidebarMainLinks: string;
  sidebarSubLinks: string;
  isOnboardingCompleteBannerOpen: boolean;
  isWhiteLabel: boolean;
  isPaymentPopupOpen: boolean;
  paymentPopupTemporaryMessage: string | undefined;
  isPaymentPopupFromBrandLimit: boolean;
  isSwitchShopPopupOpen: boolean;
  autoSelectAgencyRole: boolean;
  isHelpSetupPopupOpen: boolean;
  hasSkippedHelpSetup: boolean;
  isTaraChatAnimationEnabled: boolean;
  isChatAnimTriggered: boolean;
  userSettings: UserSettings | null;
}

const initialState: UIState = {
  dashboard: {
    currentView: DashboardViews.home,
  },
  theme: {
    type: '1',
    dark: false,
  },
  isSidebarMinimized: false,
  isSidebarDrawerOpen: false,
  sidebarMainLinks: '',
  sidebarSubLinks: '',
  isOnboardingCompleteBannerOpen: false,
  isWhiteLabel: false,
  isPaymentPopupOpen: false,
  paymentPopupTemporaryMessage: undefined,
  isPaymentPopupFromBrandLimit: false,
  isSwitchShopPopupOpen: false,
  autoSelectAgencyRole: false,
  isHelpSetupPopupOpen: false,
  hasSkippedHelpSetup: false,
  isTaraChatAnimationEnabled: true,
  isChatAnimTriggered: false,
  userSettings: null,
};

export const uiSlice = createSlice({
  name: 'UI',
  initialState,
  reducers: {
    setDashboardView: (state, action) => {
      state.dashboard.currentView = action.payload.view;
    },
    setSidebarMainLink: (state, action) => {
      state.sidebarMainLinks = action.payload;
    },
    setSidebarSubLink: (state, action) => {
      state.sidebarSubLinks = action.payload;
    },
    setAppThemeMode: (state) => {
      state.theme.dark = !state.theme.dark;
    },
    setAppThemeType: (state, action) => {
      state.theme.type = action.payload;
    },

    setIsOnboardingCompleteBannerOpen: (state, action) => {
      state.isOnboardingCompleteBannerOpen = action.payload;
    },

    toggleSidebarDrawer: (state) => {
      state.isSidebarDrawerOpen = !state.isSidebarDrawerOpen;
    },
    toggleSidebarSize: (state) => {
      state.isSidebarMinimized = !state.isSidebarMinimized;
    },
    setIsPaymentPopupOpen: (state, action) => {
      if (action.payload === false) {
        state.paymentPopupTemporaryMessage = undefined;
        state.isPaymentPopupFromBrandLimit = false;
      }
      state.isPaymentPopupOpen = action.payload;
    },
    setIsPaymentPopupFromBrandLimit: (state, action) => {
      state.isPaymentPopupFromBrandLimit = action.payload;
    },
    setPaymentPopupTemporaryMessage: (state, action) => {
      if (action.payload === undefined || action.payload === null) {
        state.paymentPopupTemporaryMessage = action.payload;
      } else {
        state.paymentPopupTemporaryMessage = String(action.payload);
      }
    },
    setIsSwitchShopPopupOpen: (state, action) => {
      state.isSwitchShopPopupOpen = action.payload;
    },
    setIsWhiteLabel: (state, action) => {
      state.isWhiteLabel = action.payload;
    },
    setAutoSelectAgencyRole: (state, action) => {
      state.autoSelectAgencyRole = action.payload;
    },
    setIsHelpSetupPopupOpen: (state, action) => {
      state.isHelpSetupPopupOpen = action.payload;
    },
    setHasSkippedHelpSetup: (state, action) => {
      state.hasSkippedHelpSetup = action.payload;
    },
    setIsTaraChatAnimationEnabled: (state, action) => {
      state.isTaraChatAnimationEnabled = action.payload;
    },
    setIsSidebarMinimized: (state, action) => {
      state.isSidebarMinimized = action.payload;
    },
    setIsChatAnimTriggered(state, action: PayloadAction<boolean>) {
      state.isChatAnimTriggered = action.payload;
    },
    setUserSettings: (state, action: PayloadAction<UserSettings | null>) => {
      state.userSettings = action.payload;

      if (action.payload?.chat_animation !== undefined) {
        state.isTaraChatAnimationEnabled = action.payload.chat_animation;
      }
    },
    updateUserSetting: (
      state,
      action: PayloadAction<{ key: keyof UserSettings; value: any }>
    ) => {
      if (state.userSettings) {
        state.userSettings[action.payload.key] = action.payload.value;
      }
    },
    clearUISlice: (state) => {
      state.isTaraChatAnimationEnabled =
        initialState.isTaraChatAnimationEnabled;
      state.isChatAnimTriggered = initialState.isChatAnimTriggered;
      state.userSettings = initialState.userSettings;
      state.isSidebarMinimized = initialState.isSidebarMinimized;
      state.isSidebarDrawerOpen = initialState.isSidebarDrawerOpen;
    },
  },
});

export const uiReducer = uiSlice.reducer;

export const {
  setDashboardView,
  setSidebarMainLink,
  setSidebarSubLink,
  setAppThemeMode,
  setIsOnboardingCompleteBannerOpen,
  toggleSidebarDrawer,
  setIsWhiteLabel,
  setAppThemeType,
  toggleSidebarSize,
  setIsPaymentPopupOpen,
  setPaymentPopupTemporaryMessage,
  setIsPaymentPopupFromBrandLimit,
  setIsSwitchShopPopupOpen,
  setAutoSelectAgencyRole,
  setIsHelpSetupPopupOpen,
  setHasSkippedHelpSetup,
  setIsTaraChatAnimationEnabled,
  setIsSidebarMinimized,
  setIsChatAnimTriggered,
  setUserSettings,
  updateUserSetting,
  clearUISlice,
} = uiSlice.actions;
